import { useEffect, useState } from "react";
import Placeholder from "@/components/Placeholder";
import {
  Star,
  Music,
  Utensils,
  Camera,
  ShoppingBag,
  Baby,
  Trophy,
  MapPin,
  Calendar,
  Clock,
  Shirt,
  Car,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";

const EVENT_DATE = new Date("2026-10-16T17:00:00+05:30").getTime();

const whyAttend = [
  { icon: Star, label: "Celebrity Guests" },
  { icon: Music, label: "Live DJ" },
  { icon: Trophy, label: "Traditional Dandiya" },
  { icon: Utensils, label: "Food Court" },
  { icon: Trophy, label: "Competitions" },
  { icon: Camera, label: "Photo Booth" },
  { icon: ShoppingBag, label: "Shopping" },
  { icon: Baby, label: "Kids Zone" },
];

const schedule = [
  { time: "4 PM", label: "Gate Opens" },
  { time: "5 PM", label: "Dance Floor Opens" },
  { time: "6 PM", label: "Opening Ceremony" },
  { time: "10 PM", label: "Closing" },
];

const details = [
  { icon: MapPin, label: "Venue", value: "Sukhwani palm lawn, near hanging bridge, Newale Wasti, Tathawade, Pimpri-Chinchwad, Maharashtra 411033" },
  { icon: Calendar, label: "Date", value: "16 – 18 October 2026" },
  { icon: Clock, label: "Time", value: "4:00 PM onwards" },
  { icon: Shirt, label: "Dress Code", value: "Traditional / Ethnic" },
  { icon: Car, label: "Parking", value: "Available on-site" },
];

const BOOKMYSHOW_URL = "https://in.bookmyshow.com/activities/royale-raas/ET00517215";
const DISTRICT_URL = "https://link.district.in/DSTRKT/ur01hvo7";
const FINKUP_URL = "https://www.finkup.com/royale-raas-2026-punes-grand-navratri-celebration/event/";

const ticketingPlatforms = [
  {
    name: "BookMyShow",
    href: BOOKMYSHOW_URL,
    testId: "bookmyshow",
    tickets: [
      { name: "Kid", price: "₹161.31" },
      { name: "Early Bird Single", price: "₹377.83" },
      { name: "Early Bird Couple", price: "₹756.73" },
      { name: "Group of 5", price: "₹1,839.33" },
      { name: "Group of 10", price: "₹3,625.63" },
    ],
  },
  {
    name: "District",
    href: DISTRICT_URL,
    testId: "district",
    tickets: [
      { name: "Kid", price: "₹172.60" },
      { name: "Early Bird Single", price: "₹384.40" },
      { name: "Early Bird Couple", price: "₹758.00" },
      { name: "Group of 5", price: "₹1,840.60" },
      { name: "Group of 10", price: "₹3,632.20" },
    ],
  },
  {
    name: "FinkUP",
    href: FINKUP_URL,
    testId: "finkup",
    tickets: [
      { name: "Kid", price: "₹186.19" },
      { name: "Early Bird Single", price: "₹436.12" },
      { name: "Early Bird Couple", price: "₹873.48" },
      { name: "Group of 5", price: "₹2,497.99" },
      { name: "Group of 10", price: "₹4,622.34" },
    ],
  },
];

function useCountdown(target) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setT({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}

function CountdownBox({ value, label, testId }) {
  return (
    <div
      data-testid={testId}
      className="glass rounded-2xl px-5 py-6 sm:px-8 sm:py-7 text-center min-w-[92px] sm:min-w-[120px]"
    >
      <div className="font-display text-4xl sm:text-6xl gold-text-gradient tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
      <div className="mt-2 text-[0.65rem] sm:text-xs tracking-[0.3em] uppercase text-neutral-400">
        {label}
      </div>
    </div>
  );
}

export default function RoyaleRaas() {
  const { d, h, m, s } = useCountdown(EVENT_DATE);

  return (
    <section
      id="royale-raas"
      data-testid="royale-raas-section"
      className="relative overflow-hidden"
    >
      {/* Background video placeholder + overlay */}
      <div className="absolute inset-0">

        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,6,6,0.75), rgba(6,6,6,0.9) 60%, #060606)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Title */}
        <div className="text-center fade-up">
          <div className="eyebrow justify-center mb-5">The Main Attraction</div>
          <h2 className="font-display text-5xl sm:text-7xl md:text-8xl leading-[0.95] tracking-tight">
            <span className="gold-text-gradient">ROYALE</span>{" "}
            <span className="italic">RAAS</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-neutral-300">
            The Grandest Dandiya Celebration.
          </p>
        </div>

        {/* Countdown */}
        <div
          data-testid="royale-countdown"
          className="mt-12 flex justify-center gap-3 sm:gap-5 flex-wrap fade-up"
        >
          <CountdownBox testId="countdown-days" value={d} label="Days" />
          <CountdownBox testId="countdown-hours" value={h} label="Hours" />
          <CountdownBox testId="countdown-minutes" value={m} label="Minutes" />
          <CountdownBox testId="countdown-seconds" value={s} label="Seconds" />
        </div>

        {/* Event details */}
        <div className="mt-16 grid md:grid-cols-3 gap-4 fade-up">
          {details.map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="glass rounded-2xl p-5 flex items-start gap-3 card-hover"
              data-testid={`royale-detail-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-10 h-10 rounded-xl border border-[var(--ee-border)] flex items-center justify-center shrink-0">
                <Icon size={18} className="text-[var(--ee-gold)]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-neutral-400">
                  {label}
                </p>
                <p className="text-sm text-neutral-100 mt-1">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* About the event */}
        <div className="mt-16 grid lg:grid-cols-5 gap-8 items-start fade-up">
          <div className="lg:col-span-3">
            <div className="eyebrow mb-4">About the Night</div>
            <h3 className="font-display text-3xl md:text-4xl">
              Three nights of{" "}
              <span className="gold-text-gradient italic">
                rhythm, colour and celebration
              </span>
            </h3>
            <div className="mt-5 glass rounded-2xl p-5">
              <p className="text-[var(--ee-gold)] text-xs uppercase tracking-[0.22em] mb-2">
                Where Tradition Dances with Luxury
              </p>
              <div className="text-neutral-300 text-sm leading-relaxed whitespace-pre-line">
Royale Raas

Where Tradition Dances with Luxury

Royale Raas is the flagship Navratri celebration by Elite Eventz, where the timeless traditions of 
Garba and Dandiya meet the elegance of a premium cultural experience. Inspired by royal grandeur, 
the event blends vibrant music, spectacular décor, immersive lighting and world-class entertainment to 
create an unforgettable festive atmosphere.
Hosted at a premium venue, Royale Raas features a high-energy dance floor, live music, authentic festive 
cuisine, interactive entertainment and stunning photo installations. Guests are encouraged to embrace 
the spirit of Navratri by dressing in their finest traditional attire, making fashion an integral part of the celebration.
Whether you're dancing with family and friends, enjoying delicious food or simply soaking in the festive energy, 
Royale Raas offers an evening filled with culture, celebration and lasting memories.
Premium Navratri celebration inspired by royal grandeur.
Live Garba & Dandiya with energetic music and performances.
Thousands of enthusiastic participants on a vibrant dance floor.
Authentic festive food court with gourmet delights.
Instagram-worthy décor, immersive lighting and photo zones.
Traditional fashion, cultural performances and interactive experiences.
Professionally curated by Elite Eventz for a seamless guest experience.
<b>Where Every Step Celebrates Tradition. Every Moment Feels Royal.</b>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="relative w-full rounded-2xl overflow-hidden border border-[var(--ee-border)] bg-black/20">
              <img
                data-testid="royale-poster"
                src="/upcoming/royale-raas/poster.jpeg"
                alt="Royale Raas — Pune Dandiya Mahotsav 2026 poster"
                loading="lazy"
                className="w-full h-auto object-contain"
                draggable={false}
              />
            </div>
          </div>
        </div>

        {/* Why attend */}
        <div className="mt-20 fade-up">
          <div className="text-center mb-8">
            <div className="eyebrow justify-center mb-3">Why Attend?</div>
            <h3 className="font-display text-3xl md:text-4xl">
              Everything you&apos;d expect —{" "}
              <span className="gold-text-gradient italic">and more</span>
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {whyAttend.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="glass rounded-2xl p-6 flex flex-col items-center text-center card-hover"
                data-testid={`royale-why-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-2xl border border-[var(--ee-border)] flex items-center justify-center">
                  <Icon size={20} className="text-[var(--ee-gold)]" />
                </div>
                <p className="mt-3 text-sm text-neutral-200">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div className="mt-20 fade-up">
          <div className="text-center mb-8">
            <div className="eyebrow justify-center mb-3">Event Schedule</div>
            <h3 className="font-display text-3xl md:text-4xl">
              From gates open to grand finale
            </h3>
          </div>
          <ol className="max-w-3xl mx-auto relative border-l border-[var(--ee-border)] ml-4 md:ml-0 md:mx-auto md:pl-0">
            {schedule.map((item) => (
              <li
                key={item.time}
                data-testid={`royale-schedule-${item.time}`}
                className="pl-8 relative py-5"
              >
                <span
                  className="absolute -left-[7px] top-7 w-3 h-3 rounded-full bg-[var(--ee-gold)]"
                  style={{ boxShadow: "0 0 0 5px rgba(212,175,55,0.15)" }}
                />
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-2xl gold-text-gradient min-w-[70px]">
                    {item.time}
                  </span>
                  <span className="text-neutral-200">{item.label}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Ticket pricing */}
        <div className="mt-20 fade-up">
          <div className="text-center mb-10">
            <div className="eyebrow justify-center mb-3">Tickets &amp; Pricing</div>
            <h3 className="font-display text-3xl md:text-4xl">
              Compare prices across our{" "}
              <span className="gold-text-gradient italic">ticketing partners</span>
            </h3>
            <p className="mt-3 text-sm text-neutral-400 max-w-xl mx-auto">
              Compare ticket prices across our official ticketing partners.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {ticketingPlatforms.map((platform) => (
              <div
                key={platform.name}
                data-testid={`royale-pricing-${platform.testId}`}
                className="glass rounded-2xl p-6 flex flex-col card-hover"
              >
                <p className="font-display text-xl text-center tracking-wide mb-5">
                  {platform.name}
                </p>
                <div className="flex-1 divide-y divide-[var(--ee-border)]">
                  {platform.tickets.map((ticket) => (
                    <div
                      key={ticket.name}
                      className="flex items-center justify-between gap-3 py-2.5 text-sm"
                    >
                      <span className="text-neutral-400">{ticket.name}</span>
                      <span className="text-neutral-100 font-semibold tabular-nums">
                        {ticket.price}
                      </span>
                    </div>
                  ))}
                </div>
                <a
                  data-testid={`royale-pricing-book-${platform.testId}`}
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-gold text-sm mt-6 w-full"
                >
                  Book on {platform.name} <ExternalLink size={14} />
                </a>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-neutral-500 text-center max-w-2xl mx-auto">
            Prices shown are based on the current ticketing platform listings and may change based on availability, taxes, or platform charges.
          </p>
        </div>

        {/* Tickets */}
        <div className="mt-20 text-center fade-up">
          <div className="eyebrow justify-center mb-4">Reserve your spot</div>
          <h3 className="font-display text-3xl md:text-4xl">
            Book on our{" "}
            <span className="gold-text-gradient italic">ticketing partners</span>
          </h3>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a
              data-testid="royale-book-bookmyshow"
              href="https://in.bookmyshow.com/activities/royale-raas/ET00517215"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold text-base relative z-20 pointer-events-auto"
              onClick={(e) => {
                if (e.currentTarget.href) {
                  window.open(e.currentTarget.href, "_blank", "noopener,noreferrer");
                }
              }}
            >
              Book on BookMyShow <ExternalLink size={16} />
            </a>
            <a
              data-testid="royale-book-district"
              href="https://link.district.in/DSTRKT/ur01hvo7"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold text-base relative z-20 pointer-events-auto"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  "https://link.district.in/DSTRKT/ur01hvo7",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              Book on District <ExternalLink size={16} />
            </a>
            <a
              data-testid="royale-book-finkup"
              href="https://www.finkup.com/royale-raas-2026-punes-grand-navratri-celebration/event/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold text-base relative z-20 pointer-events-auto"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  "https://www.finkup.com/royale-raas-2026-punes-grand-navratri-celebration/event/",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              Book on FinkUP <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
